import { resolveKafkaTarget } from '@/utils/v2-console-target/kafka'
import { resolveOrganizationTarget } from '@/utils/v2-console-target/organization'
import { resolvePulsarTarget } from '@/utils/v2-console-target/pulsar'
import {
  exact,
  fallback,
  readString,
  segment,
  type V2ConsoleRouteContext,
  type V2ConsoleTarget
} from '@/utils/v2-console-target/shared'
import { resolveWorkspaceTarget } from '@/utils/v2-console-target/workspace'

export type {
  V2ConsoleRouteContext,
  V2ConsoleTarget,
  V2FallbackDestination
} from '@/utils/v2-console-target/shared'

/**
 * Cross-repository route contract: these targets were verified against the Console v2 App Router
 * in streamnative-ai/apps/console at commit 2b8f1c9cd95dc195ea45513d51128ee5d6f9b90a.
 * The repositories do not publish a shared route manifest, so v2 route changes must update this
 * resolver and its literal-path tests together. See the feature spec for the ownership decision.
 */
export function resolveV2ConsoleTarget(context: V2ConsoleRouteContext): V2ConsoleTarget {
  const routeName = context.routeName ?? ''
  const org = readString(context.query, 'org')
  const instance = readString(context.query, 'instance')
  const tenant = readString(context.query, 'tenant')
  const namespace = readString(context.query, 'namespace')
  const topic = readString(context.query, 'topic')
  const clusterUid = readString(context.query, 'cluster')
  const consumerGroupId = readString(context.params, 'consumerGroupId')

  const homeFallback = fallback('/', 'home')
  if (routeName === 'OrganizationsPage') return exact('/')
  if (!org) return homeFallback

  const orgBase = `/orgs/${segment(org)}`
  const orgFallback = fallback(orgBase, 'organization')
  const organizationTarget = resolveOrganizationTarget({
    routeName,
    params: context.params,
    query: context.query,
    orgBase,
    instance,
    clusterUid
  })
  if (organizationTarget) return organizationTarget
  if (!instance) return orgFallback

  const instanceBase = `${orgBase}/instances/${segment(instance)}`
  const instanceFallback = fallback(instanceBase, 'instance')
  if (routeName === 'InstanceOverviewPage') return exact(instanceBase)
  if (routeName === 'InstanceConfigurationPage') return instanceFallback
  if (routeName === 'ClustersPage') return exact(`${instanceBase}/pulsar-clusters`)
  if (routeName === 'KafkaClustersPage') return exact(`${instanceBase}/kafka-clusters`)
  if (routeName === 'WorkspacesPage') return instanceFallback

  const workspaceBase = context.workspaceName
    ? `${instanceBase}/ws/${segment(context.workspaceName)}`
    : undefined
  const workspaceFallback = workspaceBase ? fallback(workspaceBase, 'workspace') : instanceFallback
  const workspaceTarget = resolveWorkspaceTarget({
    routeName,
    params: context.params,
    workspaceBase,
    workspaceFallback,
    instanceFallback
  })
  if (workspaceTarget) return workspaceTarget

  const pulsarClusterBase = context.pulsarClusterName
    ? `${instanceBase}/pulsar-clusters/${segment(context.pulsarClusterName)}`
    : undefined
  const pulsarClusterFallback = pulsarClusterBase
    ? fallback(`${pulsarClusterBase}/overview`, 'pulsarCluster')
    : instanceFallback

  const kafkaClusterBase = context.kafkaClusterName
    ? `${instanceBase}/kafka-clusters/${segment(context.kafkaClusterName)}`
    : undefined
  const kafkaClusterFallback = kafkaClusterBase
    ? fallback(`${kafkaClusterBase}/overview`, 'kafkaCluster')
    : instanceFallback

  if (routeName === 'ProvisionPage') {
    if (kafkaClusterBase) {
      return fallback(`${kafkaClusterBase}/details`, 'kafkaClusterDetails')
    }
    if (pulsarClusterBase) {
      return fallback(`${pulsarClusterBase}/details`, 'pulsarClusterDetails')
    }
    return instanceFallback
  }

  const kafkaTarget = resolveKafkaTarget({
    routeName,
    params: context.params,
    kafkaClusterBase,
    instanceFallback,
    topic,
    consumerGroupId
  })
  if (kafkaTarget) return kafkaTarget

  const pulsarTarget = resolvePulsarTarget({
    routeName,
    params: context.params,
    pulsarClusterBase,
    instanceFallback,
    tenant,
    namespace,
    topic,
    topicPersistent: context.topicPersistent,
    consumerGroupId
  })
  if (pulsarTarget) return pulsarTarget

  return context.kafkaClusterName
    ? kafkaClusterFallback
    : context.pulsarClusterName
    ? pulsarClusterFallback
    : context.workspaceName
    ? workspaceFallback
    : instanceFallback
}
